import AddressEditor from '@app/components/AddressEditor'
import AssociatedContactsSelector from '@app/components/AssociatedContactsSelector'
import DevelopmentSiteStatusSelector from '@app/components/DevelopmentSiteStatusSelector'
import EditPageContainer from '@app/components/EditPageContainer'
import { FileDropzone, FileProgress } from '@app/components/FileUpload'
import FormGroupRenderer from '@app/components/FormGroupRenderer'
import PageSection from '@app/components/PageSection'
import { DevelopmentSiteStatus } from '@app/models/enums'
import { bindFunctions } from '@app/utils/class'
import { faPlus } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { List } from 'immutable'
import { RouteComponentProps } from 'react-router-dom'
import {
  Alert,
  Button,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap'
import {
  actionCreators,
  ATTACHMENTS_IDENTIFIER,
  IDevelopmentSiteEditState,
  PHOTOS_IDENTIFIER,
} from '../modules/developmentSiteEdit'
import { AssociatedContact } from '@app/models/AssociatedContact'

type DevelopmentSiteEditPageProps = IDevelopmentSiteEditState &
  typeof actionCreators &
  RouteComponentProps<{ clientSlug: string; siteSlug?: string }>

export default class DevelopmentSiteEditPage extends React.PureComponent<DevelopmentSiteEditPageProps> {
  private teamRef: React.RefObject<HTMLFormElement>

  constructor(props: DevelopmentSiteEditPageProps) {
    super(props)
    this.teamRef = React.createRef()
    bindFunctions(this)
  }

  public componentDidMount() {
    if (this.props.match.params.siteSlug) {
      this.props.loadEditDevelopmentSite(
        this.props.match.params.clientSlug,
        this.props.match.params.siteSlug,
      )
    } else {
      this.props.loadAddDevelopmentSite(this.props.match.params.clientSlug)
    }
  }

  public componentWillReceiveProps(nextProps: DevelopmentSiteEditPageProps) {
    const nextParams = nextProps.match.params
    const thisParams = this.props.match.params
    if (
      nextParams.clientSlug !== thisParams.clientSlug ||
      nextParams.siteSlug !== thisParams.siteSlug
    ) {
      if (nextParams.siteSlug) {
        this.props.loadEditDevelopmentSite(
          nextParams.clientSlug,
          nextParams.siteSlug,
        )
      } else {
        this.props.loadAddDevelopmentSite(nextParams.clientSlug)
      }
    }
  }

  public componentWillUnmount() {
    this.props.resetState()
  }

  public render() {
    if (!this.props.isReady) {
      return (
        <DocumentTitle
          title={this.props.match.params.siteSlug ? 'Edit Site' : 'New Site'}
        >
          <Container className='my-3'>
            <div className='mb-3 text-center'>Retrieving Site Information</div>
            {this.props.error && (
              <Alert color='danger'>{this.props.error}</Alert>
            )}
          </Container>
        </DocumentTitle>
      )
    }

    return (
      <EditPageContainer
        onSaveClick={this._onSave}
        onCancelClick={this._onCancel}
        saveDisabled={false}
        title={`${this.props.site.client.name} :: ${
          this.props.match.params.siteSlug
            ? `${this.props.site.name} :: Edit`
            : 'New Site'
        }`}
      >
        <Container className='my-3'>
          {this._renderGeneralInformation()}
          {this._renderProjectInformation()}
          {this._renderTeam()}
          {this._renderPhotos()}
          {this._renderAttachments()}
        </Container>
      </EditPageContainer>
    )
  }

  private _onCancel() {
    this.props.history.goBack()
  }

  private _onSave() {
    this.props.saveDevelopmentSite(
      this.props.match.params.clientSlug,
      this.props.site,
    )
  }

  private _renderAttachments() {
    const dropzoneStyle: React.CSSProperties = {
      borderColor: 'rgb(102, 102, 102)',
      borderRadius: '5px',
      borderStyle: 'dashed',
      borderWidth: '1px',
      display: 'table',
      height: '100px',
      width: '100%',
    }

    const dropzoneActiveStyle: React.CSSProperties = {
      borderColor: '#9aa41a',
      borderWidth: '2px',
    }

    return (
      <PageSection title='Attachments'>
        <FileDropzone
          activeStyle={dropzoneActiveStyle}
          style={dropzoneStyle}
          identifier={ATTACHMENTS_IDENTIFIER}
        >
          <p
            style={{
              display: 'table-cell',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            Drag and drop or click here to select files
          </p>
        </FileDropzone>
        <FileProgress
          dataSource={this.props.site.attachments}
          identifier={ATTACHMENTS_IDENTIFIER}
        />
      </PageSection>
    )
  }

  private _renderGeneralInformation() {
    return (
      <PageSection title='Development Site'>
        <FormGroupRenderer labelText='Client'>
          {this.props.site.client.name}
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Status'>
          <DevelopmentSiteStatusSelector
            onChange={this.props.setStatus}
            value={this.props.site.status}
          />
        </FormGroupRenderer>
        <FormGroupRenderer
          labelText={
            this.props.site.status === DevelopmentSiteStatus.Potential
              ? 'Market'
              : 'Address'
          }
        >
          <AddressEditor
            cityStateOnly={
              this.props.site.status === DevelopmentSiteStatus.Potential
            }
            onChange={this.props.setAddress}
            value={this.props.site.address}
          />
        </FormGroupRenderer>
        <FormGroupRenderer
          autoComplete='nope'
          labelText='Name'
          name='Name'
          onChange={this.props.setName}
          required
          value={this.props.site.name}
        />
      </PageSection>
    )
  }

  private _renderPhotos() {
    const dropzoneStyle: React.CSSProperties = {
      borderColor: 'rgb(102, 102, 102)',
      borderRadius: '5px',
      borderStyle: 'dashed',
      borderWidth: '1px',
      display: 'table',
      height: '100px',
      width: '100%',
    }

    const dropzoneActiveStyle: React.CSSProperties = {
      borderColor: '#9aa41a',
      borderWidth: '2px',
    }

    return (
      <PageSection title='Photos'>
        <FileDropzone
          activeStyle={dropzoneActiveStyle}
          style={dropzoneStyle}
          identifier={PHOTOS_IDENTIFIER}
        >
          <p
            style={{
              display: 'table-cell',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            Drag and drop or click here to select files
          </p>
        </FileDropzone>
        <FileProgress
          dataSource={this.props.site.photos}
          identifier={PHOTOS_IDENTIFIER}
          mainPhotoId={this.props.site.coverPhotoId}
          onSetMainPhoto={this.props.setMainPhoto}
        />
      </PageSection>
    )
  }

  private _renderProjectInformation() {
    if (this.props.site.status === DevelopmentSiteStatus.Potential) {
      return null
    }

    return (
      <PageSection title='Project Information'>
        <FormGroupRenderer
          labelText='Sales Price'
          onChange={this.props.setSalesPrice}
          step='0.01'
          type='number'
          value={this.props.site.salesPrice}
        />
        <FormGroupRenderer
          labelText='Construction Price'
          onChange={this.props.setConstructionPrice}
          step='0.01'
          type='number'
          value={this.props.site.constructionPrice}
        />
      </PageSection>
    )
  }

  private _renderTeam() {
    return (
      <PageSection
        title='Team'
        actions={
          <Button size='sm' color='primary' onClick={this.props.beginAddTeam}>
            <FontAwesomeIcon icon={faPlus} fixedWidth />
            Add Team
          </Button>
        }
      >
        {this.props.site.team
          .sortBy((t) => (t !== undefined ? t.name : ''))
          .toArray()
          .map((t) => (
            <FormGroupRenderer key={t.key} labelText={t.name}>
              <AssociatedContactsSelector
                onChange={(newValue: List<AssociatedContact>) =>
                  this.props.setTeamRoleMembers(t.key, newValue)
                }
                placeholder={`Add connections to ${t.name} role...`}
                value={t.members}
              />
            </FormGroupRenderer>
          ))}
        <Modal
          isOpen={this.props.showTeamModal}
          toggle={() => this.props.cancelAddTeam()}
          size='lg'
        >
          <ModalHeader toggle={() => this.props.cancelAddTeam()}>
            New Team
          </ModalHeader>
          <ModalBody
            style={{ height: '300px', maxHeight: '70vh', overflowY: 'auto' }}
          >
            <form ref={this.teamRef}>
              <div>
                <FormGroupRenderer
                  labelText='Team Name'
                  onChange={(newValue) => this.props.setNewTeamName(newValue)}
                  type='text'
                  value={this.props.newTeamName}
                />
                <FormGroupRenderer labelText='Team Members'>
                  <AssociatedContactsSelector
                    onChange={(newValue: List<AssociatedContact>) =>
                      this.props.setNewTeamMembers(newValue)
                    }
                    placeholder={`Add connections to ${this.props.newTeamName} role...`}
                    value={this.props.newTeamMembers}
                  />
                </FormGroupRenderer>
              </div>
            </form>
          </ModalBody>
          <ModalFooter>
            <Button
              color='secondary'
              onClick={() => this.props.cancelAddTeam()}
              outline
            >
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={() => {
                if (
                  this.teamRef.current &&
                  this.teamRef.current.checkValidity()
                ) {
                  this.props.endAddTeam()
                }
              }}
            >
              OK
            </Button>
          </ModalFooter>
        </Modal>
      </PageSection>
    )
  }
}
